import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { AlertService } from './alert.service';
import { NotificationService } from './notification.service';
import { NotificationResponse, NotificationType } from '../model/fss.api';
import { AUTH_TOKEN_STORAGE_KEY } from '../constants';
import { environment } from '../../../environment/environment';

const NOTIFICATION_RECEIVED_EVENT = 'NOTIFICATION_RECEIVED';

@Injectable({
  providedIn: 'root',
})
export class SignalrService {
  private _hubConnection: signalR.HubConnection | undefined;

  constructor(
    private alertService: AlertService,
    private notificationService: NotificationService,
  ) {}

  public startConnection = async (): Promise<void> => {
    if (this._hubConnection) {
      return;
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.apiBaseUrl}/notifications`, {
        accessTokenFactory: () =>
          localStorage.getItem(AUTH_TOKEN_STORAGE_KEY) ?? '',
      })
      .withAutomaticReconnect()
      .build();

    connection.onreconnected(() => {
      this.notificationService.loadUnreadCount();
    });

    connection.on(NOTIFICATION_RECEIVED_EVENT, (raw: unknown) => {
      const notification = NotificationResponse.fromJS(raw);

      this.notificationService.addFromPush(notification);
      this.alertService.notifyNoticationChanged();
      this.alertService.addInfoMsg(notification.body ?? '', notification.title);

      if (notification.type === NotificationType.NEW_PACKAGE_REQUEST) {
        this.alertService.notifyRequestCountChanged();
      }
    });

    this._hubConnection = connection;

    try {
      await connection.start();
      console.log('SignalR connected');
    } catch (err) {
      console.error('SignalR failed to connect', err);
      this._hubConnection = undefined;
    }
  };

  public closeConnection = async (): Promise<void> => {
    if (!this._hubConnection) {
      return;
    }

    const connection = this._hubConnection;
    this._hubConnection = undefined;
    await connection.stop();
  };
}
